import { X } from 'lucide-react';
import * as React from 'react';
import { Link, useRouteMatch } from 'react-router-dom';

import { ReactComponent as ClueyPlusLogoSmIcon } from '../../../assets/icons/brand-logo-sm.svg';
import { ReactComponent as ClueyPlusLogoIcon } from '../../../assets/icons/brand-logo.svg';
import { cn } from '../../common/helpers/cn';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '../../components/ui/sidebar';
import env from '../../legacy/env';
import { PATH_HUB_HOME } from '../../legacy/pagePath';
import { AccountSwitcher } from '../account-switcher';
import { type AppSidebarItem } from './navItems';
import { useAppSidebar } from './useAppSidebar';

type NavMenuItemProps = {
  item: AppSidebarItem;
  onNavigate: () => void;
};

// Renders a single nav entry. Link items derive active state from the route.
function NavMenuItem({ item, onNavigate }: NavMenuItemProps) {
  const { open: sidebarOpen } = useSidebar();
  const Icon = item.icon;
  const match = useRouteMatch(item.to ? { path: item.to, exact: item.exact } : { path: '__never__' });
  const isActive = Boolean(item.to) && Boolean(match);
  const menuItemClassName = cn('!px-0', sidebarOpen ? '' : 'flex justify-center');
  const menuButtonClassName = cn(
    'flex h-fit flex-row gap-3',
    sidebarOpen
      ? ''
      : '!p-0 group-data-[collapsible=icon]:!h-[32px] group-data-[collapsible=icon]:!w-[32px] group-data-[collapsible=icon]:!p-0'
  );

  if (item.to) {
    return (
      <SidebarMenuItem className={menuItemClassName}>
        <SidebarMenuButton asChild tooltip={item.label} isActive={isActive} className={menuButtonClassName}>
          <Link to={item.to} onClick={onNavigate}>
            <Icon className="!h-8 !w-8" />
            <span className="text-base font-medium text-grey-600">{item.label}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem className={menuItemClassName}>
      <SidebarMenuButton
        tooltip={item.label}
        className={menuButtonClassName}
        onClick={() => {
          item.onClick?.();
          onNavigate();
        }}
      >
        <Icon className="!h-8 !w-8" />
        <span className="text-base font-medium text-grey-600">{item.label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { open: sidebarOpen, isMobile, setOpenMobile } = useSidebar();
  const { items, bookSessionItem, enrolmentModal } = useAppSidebar();

  const closeMobileMenu = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <>
      <Sidebar collapsible="icon" {...props}>
        <SidebarHeader className={cn('p-5', sidebarOpen ? 'px-6 pt-10' : 'pt-8')}>
          <SidebarMenu>
            <SidebarMenuItem
              className={cn(isMobile ? 'flex items-center justify-between' : 'flex justify-center', '!px-0')}
            >
              <SidebarMenuButton asChild size="lg" tooltip="Hub" className="hover:!bg-transparent">
                <Link to={PATH_HUB_HOME} onClick={closeMobileMenu} className="!px-0">
                  {sidebarOpen || isMobile ? (
                    <ClueyPlusLogoIcon alt="Cluey Plus Logo" className="!h-8 !w-[100px]" />
                  ) : (
                    <ClueyPlusLogoSmIcon alt="Cluey Plus Logo" className="!h-auto !w-auto" />
                  )}
                </Link>
              </SidebarMenuButton>
              {isMobile && (
                <button
                  type="button"
                  aria-label="Close sidebar"
                  onClick={closeMobileMenu}
                  className="text-grey-600/70 cursor-pointer hover:text-grey-600"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </SidebarMenuItem>
          </SidebarMenu>
          {sidebarOpen && (
            <div className="border-b border-grey-600 py-3 font-[Merriweather] text-lg font-bold">Customer Hub</div>
          )}
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup className="!px-0">
            <SidebarMenu className={sidebarOpen ? 'px-6' : '!gap-4'}>
              {items.map((item) => (
                <NavMenuItem key={item.key} item={item} onNavigate={closeMobileMenu} />
              ))}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="!px-0">
          {bookSessionItem && (
            <SidebarMenu className={sidebarOpen ? 'px-6' : '!gap-4'}>
              <NavMenuItem item={bookSessionItem} onNavigate={closeMobileMenu} />
            </SidebarMenu>
          )}
          <SidebarMenu className={cn('px-3 pb-5', sidebarOpen ? 'px-6' : 'items-center')}>
            <AccountSwitcher />
          </SidebarMenu>

          {sidebarOpen ? (
            <div className="pl-3 text-xs text-muted-foreground">Version {env.REACT_APP_VERSION}</div>
          ) : (
            <div className="mx-auto flex text-[8px] text-muted-foreground">v{env.REACT_APP_VERSION}</div>
          )}
        </SidebarFooter>

        <SidebarRail />
      </Sidebar>

      <enrolmentModal.SelectEnrolmentModal
        position="top"
        open={enrolmentModal.open}
        closeModal={enrolmentModal.closeModal}
        enrolmentLinkTo={enrolmentModal.enrolmentLinkTo}
        subtitle="Which enrolment do you need to book a session for?"
        filterEnrolmentType="Course"
      />
    </>
  );
}
